import { Component, Input, OnChanges } from '@angular/core';
import { ColorPalette, PageSection } from '../../../models/end-client-reporting-package';
import type {
  StageExplainerExtraData,
} from '../../../models/end-client-reporting-component-types/stage-explainer';
import { ChartSeries, ReportBlock, chartColors } from '../report-aggregation';
import type { BlockPatch } from '../report-design-edit';
import { Spectrum, spectrumOf, withCaption } from './spectrum-band';
import { STAGE_CARD_STYLES, STAGE_MIN_WIDTH, stageRowMinWidth, stageInk, stagePartFill } from './stage-card';

/** What an empty panel says while somebody is still writing it. */
const PLACEHOLDER = 'Untitled';


interface Part {
  heading: string;
  points: string[];
  fill: string;
  ink: string;
}

interface Panel {
  title: string;
  fill: string;
  ink: string;
  parts: Part[];
}

/**
 * Explainer panel: a row of panels that teaches a reader how to read something.
 *
 * The only module here whose content is entirely written and whose job is to be read before a chart
 * rather than instead of one. It counts nothing, takes no question, and is in none of the states the
 * block host reports -- it is the same family as the executive summary and the recommended actions,
 * where a claim about what something means is not a thing a survey can answer.
 *
 * Deliberately not coupled to the chart it explains. A panel that read its headings from a ladder
 * block would keep the two in step, at the cost of breaking when that block is deleted or retyped;
 * prose that is written stays written.
 */
/**
 * The width an explainer needs to draw its narrowest stage at its minimum: a panel is as wide as its
 * parts, by the same sum as the matrix it explains. From the block alone -- the panels as drawn,
 * which for a paired explainer are its matrix's stages -- so the deck can widen the module's column
 * before the module exists. None in a compare column, where every column shares the width.
 */
export function explainerMinWidth(block: PageSection, compact: boolean): number {
  if (compact || block.reportType !== 'stage-explainer') return 0;
  const panels = block.reportExtraData.panels ?? [];
  if (!panels.length) return 0;
  return stageRowMinWidth(panels.map(panel => panel.parts?.length || 1), 0, 12);
}

@Component({
  selector: 'app-explainer-panel-block',
  standalone: false,
  template: `
    <div class="explainer" [class.compact]="compact">
      @if (panels.length) {
        <!-- Never narrower than its stages need, by the same sum as the matrix it explains, so a
             stage is as wide on both pages. No scroll of its own: the deck gives the column this
             width and scrolls sideways itself. -->
        <div [style.min-width.px]="minWidth || null">
        <div class="panels">
          @for (panel of panels; track $index; let p = $index) {
            <section class="stage-card panel" [style.flexGrow]="panel.parts.length || 1">
              <h4 class="stage-head" [style.background]="panel.fill" [style.color]="panel.ink">
                @if (editing && !contentDerived) {
                  <input type="text"
                         class="inline w-100"
                         [style.color]="panel.ink"
                         maxlength="40"
                         [attr.aria-label]="'Panel ' + (p + 1) + ' title'"
                         placeholder="Stage name"
                         [value]="titleOf(p)"
                         (input)="setPanelTitle(p, $any($event.target).value)">
                } @else {
                  {{ panel.title }}
                }
              </h4>

              @if (panel.parts.length) {
                <div class="parts" [style.--parts]="panel.parts.length">
                  @for (part of panel.parts; track $index; let i = $index) {
                    <div class="part">
                      <h5 class="part-head" [style.background]="part.fill" [style.color]="part.ink">
                        @if (editing && !contentDerived) {
                          <input type="text"
                                 class="inline w-100"
                                 [style.color]="part.ink"
                                 maxlength="30"
                                 [attr.aria-label]="'Part ' + (i + 1) + ' of panel ' + (p + 1)"
                                 placeholder="Part name"
                                 [value]="headingOf(p, i)"
                                 (input)="setPartHeading(p, i, $any($event.target).value)">
                        } @else {
                          {{ part.heading }}
                        }
                      </h5>

                      @if (editing) {
                        <!-- One point per line, which is how somebody writes a short list. A row of
                             inputs with its own add and remove buttons is more chrome than content. -->
                        <textarea class="points-input"
                                  rows="4"
                                  [attr.aria-label]="'Points for part ' + (i + 1)"
                                  placeholder="One point per line"
                                  [value]="pointsText(p, i)"
                                  (input)="setPoints(p, i, $any($event.target).value)"></textarea>
                      } @else {
                        <ul class="points list-unstyled mb-0">
                          @for (point of part.points; track $index) {
                            <li>{{ point }}</li>
                          }
                        </ul>
                      }
                    </div>
                  }
                </div>
              }

              @if (editing) {
              }
            </section>
          }
        </div>

        @if (bands.bands.length) {
          <!-- One arrow across the width rather than a row of blocks: the bands are a direction of
               travel, and a reader should see that before reading a word of them. -->
          <div class="spectrum spectrum-inset">
            <div class="arrow"
                 [style.background]="bands.background"
                 [style.clipPath]="bands.clipPath">
              @for (band of bands.bands; track $index) {
                <span class="band" [style.flexGrow]="band.grow">
                  <!-- One caption per panel, written in place -- unless the band is its matrix's. -->
                  @if (editing && !contentDerived) {
                    <input type="text" class="ecr-inline-input" maxlength="60"
                           [attr.aria-label]="'Caption under ' + (panels[$index]?.title ?? 'this panel')"
                           [placeholder]="'Under ' + (panels[$index]?.title ?? 'this panel')"
                           [value]="captionText($index)"
                           (input)="setCaption($index, $any($event.target).value)">
                  } @else {
                    {{ band.text }}
                  }
                </span>
              }
            </div>
          </div>
        }
        </div>
      } @else if (!editing) {
        <p class="grey4 nav-font12 mb-0">
          No panels yet. Turn on <em>Edit Report</em> to write them.
        </p>
      }

      @if (editing && contentDerived) {
        <p class="note grey4 mb-0 mt-2">
          <i class="fas fa-circle-info mr-1" aria-hidden="true"></i>The stages, their parts and the
          direction band come from the stage matrix this explains. Edit them there; the notes
          under each part are written here.
        </p>
      }

    </div>
  `,
  styles: [STAGE_CARD_STYLES, `
    :host { display: block; }

    /* Sized to fill a page rather than a strip: this module is usually the only thing on its own
       page, facing the chart it explains, and a reader meets it before any figures. */
    .explainer { font-size: 15px; }
    .explainer.compact { font-size: 11px; }

    /* One row, as the matrix is, rather than wrapping onto a second row the matrix does not have. */
    /* Inset by the ladder's lead so a stage comes out the same width on both pages, split evenly
       either side rather than all on the left: the ladder spends that lead on row labels and its
       Total column, and the explainer has nothing to put there. */
    .panels {
      display: flex;
      gap: 12px;
      align-items: flex-start;
      margin-inline: calc(var(--stage-lead) / 2);
    }
    /* Width in proportion to the parts inside it, so a stage of two rungs is half the width of one
       of four -- the same split the ladder makes, where a stage is as wide as the columns it spans.
       Basis 0 so the grow factors decide it outright; the min-width is the ladder's own stage
       minimum, a floor under the row's own minWidth. */
    .panel {
      flex: 1 1 0;
      min-width: ${STAGE_MIN_WIDTH}px;
    }

    /* Centred, like the ladder's stage heading: the two face each other in the deck and a heading
       that sits left on one page and centre on the other reads as two different templates. */

    /* The parts sit side by side inside their panel, which is what makes a pair of rungs read as a
       pair rather than as two more panels. */
    /* A hairline of the card's own white between the headers and above them, which is what the
       ladder's border-spacing gives its cells. Without it two headers butt into one block. */
    .parts {
      display: grid;
      grid-template-columns: repeat(var(--parts), 1fr);
      gap: 2px;
      margin-top: 2px;
    }
    /* The band runs under the panels, so it is inset with them. */
    .spectrum-inset { margin-inline: calc(var(--stage-lead) / 2); }

    .points {
      padding: 24px 16px;
      font-size: 0.95em;
      line-height: 1.7;
      /* Panels of different lengths still reach the same depth, so the row reads as one band of
         cards rather than as a ragged edge. */
      min-height: 260px;
    }
    .explainer.compact .points { padding: 14px 10px; font-size: 0.88em; min-height: 0; }
    /* Each point is its own statement, not a line of a paragraph, so they sit well apart. */
    .points li { margin-bottom: 22px; }
    .explainer.compact .points li { margin-bottom: 12px; }
    .points li:last-child { margin-bottom: 0; }
    /* A bullet drawn rather than a list-style marker, so the text wraps flush under itself. */
    .points li::before {
      content: '•';
      margin-right: 6px;
      color: var(--nav-grey3, #999);
    }


    .points-input {
      border: 1px solid var(--nav-grey1, #f2f2f2);
      border-radius: 4px;
      padding: 6px 8px;
      margin: 10px 10px 0;
      width: calc(100% - 20px);
      font: inherit;
      font-size: 0.88em;
      resize: vertical;
    }

    /* The tools themselves are in the shared sheet; where this one sits is the panel's own. */
    .part .drop { margin: 4px 0 10px 10px; }


  `],
})
export class ExplainerPanelBlockComponent implements ReportBlock, OnChanges {
  /** The width the row needs -- see explainerMinWidth, which the deck asks the same question of. */
  get minWidth(): number {
    return explainerMinWidth(this.block, !!this.compact);
  }

  @Input({ required: true }) block!: PageSection;
  /** Declared because every block receives it; this one charts nothing and never reads it. */
  @Input({ required: true }) series!: ChartSeries[];
  @Input({ required: true }) palette!: ColorPalette;
  @Input() compact?: boolean;
  /** Set while the deck is in edit mode: every word here is written in place, as on the summary. */
  @Input() editing?: boolean;
  @Input() onPatch?: (patch: BlockPatch) => void;
  /**
   * The headings and the band are read off the ladder this explains, so they are shown here rather
   * than edited. Without this the inputs would still accept typing and the next render would throw
   * it away -- the derivation runs on every render, up in the deck.
   */
  @Input() contentDerived?: boolean;

  panels: Panel[] = [];
  spectrum: string[] = [];
  bands: Spectrum = { background: 'none', clipPath: 'none', bands: [] };

  private fills: string[] = [];

  ngOnChanges() {
    this.build();
  }

  private build() {
    this.panels = [];
    this.spectrum = [];
    this.bands = { background: 'none', clipPath: 'none', bands: [] };
    if (this.block.reportType !== 'stage-explainer') return;

    const extra = this.block.reportExtraData;
    const written = extra.panels ?? [];
    this.fills = chartColors(this.palette, Math.max(1, written.length));

    this.panels = written.map((panel, index) => {
      const fill = this.fills[index % this.fills.length];
      // Read off the fill rather than assumed: the palette is the client's, and a pale brand colour
      // with white text on it is the one thing nobody can read.
      const ink = stageInk(fill);

      return {
        title: panel.title?.trim() || PLACEHOLDER,
        fill,
        ink,
        parts: (panel.parts ?? []).map((part, at) => {
          // Each part after the first is a shade lighter, so a pair reads as first and second rather
          // than as two of the same -- exactly as the ladder shades the rungs of a stage.
          const shade = stagePartFill(fill, at);
          return {
            heading: part.heading?.trim() || PLACEHOLDER,
            points: (part.points ?? []).map(point => point.trim()).filter(point => !!point),
            fill: shade,
            ink: stageInk(shade),
          };
        }),
      };
    });

    // One band per panel, as the ladder draws one per stage: the band names what the panel above it
    // is, so a fifth caption under four panels would be naming nothing. Extra captions are ignored;
    // a panel with none keeps its place in the row with an empty band.
    const captions = (extra.spectrum ?? []).map(band => band.trim());
    // While editing an explainer that owns its band, one under every panel to type into.
    const writable = !!this.editing && !this.contentDerived && this.panels.length > 0;
    this.spectrum = captions.some(band => !!band) || writable
      ? (this.panels.length
        ? this.panels.map((panel, index) => captions[index] ?? '')
        : captions.filter(band => !!band))
      // Nothing written is no band at all, rather than a row of empty arrows.
      : [];
    this.bands = spectrumOf(
      this.spectrum,
      index => this.bandFill(index),
      this.panels.map(panel => panel.parts.length || 1),
    );
  }

  captionText(index: number): string {
    return this.block.reportType === 'stage-explainer'
      ? this.block.reportExtraData.spectrum?.[index] ?? ''
      : '';
  }

  /** The caption under one panel. Positional, so patched whole; trailing blanks are not kept. */
  setCaption(index: number, caption: string) {
    if (this.block.reportType !== 'stage-explainer') return;
    this.onPatch?.({ spectrum: withCaption(this.block.reportExtraData.spectrum, index, caption) });
  }

  /** A band takes its panel's colour, so the two rows read as one scheme. */
  bandFill(index: number): string {
    return this.fills[index % Math.max(1, this.fills.length)] ?? 'var(--nav-grey3, #999)';
  }


  // --- Writing it -----------------------------------------------------------------------------
  //
  // The inputs are bound to what is *stored* rather than to what is drawn, because the drawn value
  // substitutes "Untitled" for a blank one -- bound to that, clearing a title would type the
  // placeholder into the box. Every change patches the whole `panels` array: it is a nested
  // structure, and a partial patch has nowhere to say which panel it belongs to.

  titleOf(panel: number): string {
    return this.written()[panel]?.title ?? '';
  }

  headingOf(panel: number, part: number): string {
    return this.written()[panel]?.parts?.[part]?.heading ?? '';
  }

  /** One point per line, which is how somebody writes a short list. */
  pointsText(panel: number, part: number): string {
    return (this.written()[panel]?.parts?.[part]?.points ?? []).join('\n');
  }



  setPanelTitle(panel: number, title: string) {
    this.patch(panels => {
      panels[panel] = { ...panels[panel], title };
    });
  }

  setPartHeading(panel: number, part: number, heading: string) {
    this.patch(panels => {
      const parts = [...(panels[panel].parts ?? [])];
      parts[part] = { ...parts[part], heading };
      panels[panel] = { ...panels[panel], parts };
    });
  }

  setPoints(panel: number, part: number, text: string) {
    this.patch(panels => {
      const parts = [...(panels[panel].parts ?? [])];
      // Split but not trimmed of empties here: a blank line mid-typing is somebody about to write
      // the next point, and dropping it would move the cursor. The blanks go when it is drawn.
      parts[part] = { ...parts[part], points: text.split('\n') };
      panels[panel] = { ...panels[panel], parts };
    });
  }






  /** What is stored, which is what the inputs show and what a patch is built from. */
  private written(): NonNullable<StageExplainerExtraData['panels']> {
    return this.block.reportType === 'stage-explainer'
      ? this.block.reportExtraData.panels ?? []
      : [];
  }

  private patch(change: (panels: StageExplainerExtraData['panels']) => void) {
    const panels = this.written().map(panel => ({
      ...panel,
      parts: panel.parts?.map(part => ({ ...part, points: [...(part.points ?? [])] })),
    }));
    change(panels);
    this.onPatch?.({ panels });
  }
}
